/**
 * useVisualViewport — publishes the on-screen keyboard's height as the CSS
 * variable `--kb` on <html> while a modal is mounted.
 *
 * iOS overlays the keyboard on top of `position: fixed` content (the fixed box
 * keeps its full height; nothing reflows), so the only reliable signal for "how
 * much of the bottom is covered" is the VisualViewport API:
 *
 *     kb = innerHeight - visualViewport.height - visualViewport.offsetTop
 *
 * Modals read `--kb` in plain CSS to lift their footer above the keyboard. They
 * pin themselves to `inset-0` for the full viewport rather than trying to match
 * the visual viewport's height — that earlier approach left the dialog short of
 * the screen bottom (and the toolbar peeking out beneath it) whenever the visual
 * viewport reported less than the full screen.
 */

import { useEffect } from "react";

export function useVisualViewport(): void {
  useEffect(() => {
    const vv = window.visualViewport;
    const root = document.documentElement;
    if (!vv) return;
    const set = () => {
      const kb = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      root.style.setProperty("--kb", `${kb}px`);
    };
    set();
    vv.addEventListener("resize", set);
    vv.addEventListener("scroll", set);
    return () => {
      vv.removeEventListener("resize", set);
      vv.removeEventListener("scroll", set);
      // Reset so a re-opened modal doesn't briefly inherit a stale keyboard inset.
      root.style.setProperty("--kb", "0px");
    };
  }, []);
}
